import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import OfferCarousel from "../../components/OfferCarousel";
import ProductCarousel from "../../components/ProductCarousel";
import { useCompany } from "../../contexts/CompanyContext";
import { api } from "../../lib/api";
import { Flame, Droplets, ArrowRight, MessageCircle, MapPin, Clock, Package, Heart } from "lucide-react";import { Button } from "../../components/ui/button";
import GasCylinderIcon from "../../components/icons/GasCylinderIcon";
import { BottomSheet } from "../../components/ui/bottom-sheet";
import { Check, ChevronDown, Search } from "lucide-react";
import { whatsappUrl, waTemplates } from "../../lib/whatsapp";
import { companyStatus } from "../../lib/hours";

export default function ClientHome() {
  const { companies, currentCompany, setCurrentCompany } = useCompany();
  const [offers, setOffers] = useState([]);
  const [products, setProducts] = useState([]);
  const [mostOrdered, setMostOrdered] = useState([]);
  const [chooseOpen, setChooseOpen] = useState(false);
  const [stationsOpen, setStationsOpen] = useState(false);
  const [companyPickerOpen, setCompanyPickerOpen] = useState(false);
  const [companyQuery, setCompanyQuery] = useState("");
  const [stations, setStations] = useState([]);
  const [mostUsedAddr, setMostUsedAddr] = useState(null);
  // BottomSheet de confirmación cuando "Pedir otra vez" requiere
  // cambiar de empresa (el último pedido fue en una tienda distinta
  // a la activa). Guardamos la URL ya armada para navegar al
  // confirmar, y los datos del preview para render.
  const [reorderConfirm, setReorderConfirm] = useState(null); // { url, targetCompany, items }
  // Hero buttons depend on products + stations data. Render skeletons until
  // both fetches resolve so the user doesn't see fallbacks (e.g. "WhatsApp")
  // briefly replace the real primary buttons when data arrives.
  const [heroLoaded, setHeroLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentCompany) return;
    setHeroLoaded(false);
    api.get(`/offers?company_id=${currentCompany.company_id}`).then((r) => setOffers(r.data)).catch(() => {});
    api
      .get(`/products/most-ordered?company_id=${currentCompany.company_id}&limit=6`)
      .then((r) => setMostOrdered((r.data || []).filter((p) => p.kind !== "estacionario" && p.channel !== "estacion")))
      .catch(() => setMostOrdered([]));
    // These two feed the hero — wait until both settle.
    const p1 = api.get(`/products?company_id=${currentCompany.company_id}`).then((r) => setProducts(r.data)).catch(() => setProducts([]));
    const p2 = api.get(`/public/stations?company_id=${currentCompany.company_id}`).then((r) => setStations(r.data)).catch(() => setStations([]));
    Promise.all([p1, p2]).finally(() => setHeroLoaded(true));
  }, [currentCompany]);

  // Tarjeta destacada "Pedir a mi dirección más usada" — la calcula el
  // backend agregando por `client_id` y tomando el address_id con más
  // pedidos. No depende de la empresa activa (la dirección es del
  // cliente, no de la empresa).
  useEffect(() => {
    api.get("/addresses/most-used").then((r) => setMostUsedAddr(r.data || null)).catch(() => setMostUsedAddr(null));
  }, []);

  const pickProduct = (p) => {
    const kind = p.kind === "bulk" ? "bulk" : "cylinder";
    navigate(`/app/order/new?kind=${kind}&preselect=${encodeURIComponent(p.product_id)}&fresh=1`);
  };

  // Filter out station-kind products from the main catalog (they're shown
  // separately in the "Nuestras estaciones" sheet).
  const deliverable = products.filter((p) => p.kind !== "estacionario" && p.channel !== "estacion");
  const stationProducts = products.filter((p) => p.kind === "estacionario" || p.channel === "estacion");
  // Merged catalog: bulk first (per user preference) then cylinders.
  const catalog = [...deliverable.filter((p) => p.kind === "bulk"), ...deliverable.filter((p) => p.kind === "cylinder")];

  const status = companyStatus(currentCompany);
  const nextOpenText = status.nextOpen
    ? `${status.nextOpen.dayLabel} ${status.nextOpen.date ? status.nextOpen.date.toLocaleDateString("es", { day: "numeric", month: "short" }) : ""} a las ${status.nextOpen.time}`.replace(/  +/g, " ")
    : null;

  // Representative product per kind → used to paint a real photo + price
  // on the hero card. Picks the cheapest in-kind product with an image.
  const pickRep = (kind) => {
    const list = deliverable.filter((p) => p.kind === kind);
    if (list.length === 0) return null;
    const withImg = list.filter((p) => p.image_url);
    const pool = withImg.length ? withImg : list;
    // Cheapest first so the hero shows a "desde" price.
    return pool.slice().sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0))[0];
  };
  const repBulk = pickRep("bulk");
  const repCylinder = pickRep("cylinder");
  const stationRep = stationProducts.find((p) => p.image_url) || stations[0] || null;

  // La imagen del hero respeta la decisión explícita del admin:
  //  1) Si activó "Sin imagen" para ese kind → null (HeroButton mostrará el icono por defecto sobre fondo color empresa).
  //  2) Si subió una imagen → esa URL.
  //  3) Si nunca configuró nada → fallback a la imagen del producto representativo (mantiene compat).
  const heroImageBulk = currentCompany?.hero_default_bulk
    ? null
    : (currentCompany?.hero_image_bulk || repBulk?.image_url || null);
  const heroImageCylinder = currentCompany?.hero_default_cylinder
    ? null
    : (currentCompany?.hero_image_cylinder || repCylinder?.image_url || null);
  const heroImageStation = currentCompany?.hero_default_station
    ? null
    : (currentCompany?.hero_image_station || stationRep?.image_url || stationRep?.cover_url || null);

  // Hero buttons: show each primary only if the company has relevant content.
  // Pad with fallbacks (schedule / WhatsApp / my orders) so the hero always
  // renders 3 prominent CTAs — keeps the grid balanced for any company setup.
  const primaries = [
    {
      key: "bulk",
      available: !currentCompany?.channel_hidden_bulk && deliverable.some((p) => p.kind === "bulk"),
      onClick: () => navigate("/app/order/new?kind=bulk&fresh=1"),
      icon: Flame,
      title: "Gas",
      subtitle: "Por litros o kilogramos",
      cta: "Elegir",
      variant: "primary",
      image: heroImageBulk,
      price: repBulk?.price,
      productId: repBulk?.product_id,
      testid: "choose-bulk",
    },
    {
      key: "cylinder",
      available: !currentCompany?.channel_hidden_cylinder && deliverable.some((p) => p.kind === "cylinder"),
      onClick: () => navigate("/app/order/new?kind=cylinder&fresh=1"),
      icon: GasCylinderIcon,
      title: "Cilindros",
      subtitle: "20 kg, 35 kg o 45 kg",
      cta: "Elegir",
      variant: "primary",
      image: heroImageCylinder,
      price: repCylinder?.price,
      productId: repCylinder?.product_id,
      testid: "choose-cylinder",
    },
    {
      key: "stations",
      available: !currentCompany?.channel_hidden_station && (stations.length > 0 || stationProducts.length > 0),
      onClick: () => setStationsOpen(true),
      icon: MapPin,
      title: "Estaciones",
      subtitle: "Visítanos",
      cta: "Ver",
      variant: "primary",
      image: heroImageStation,
      price: null,
      productId: `station-${stations.length || 0}`,
      testid: "choose-stations",
    },
  ];
  const waHref = currentCompany?.whatsapp && whatsappUrl(currentCompany.whatsapp)
    ? whatsappUrl(currentCompany.whatsapp, waTemplates.clientToCompany(currentCompany.name))
    : null;
  const fallbacks = [
    {
      key: "schedule",
      onClick: () => navigate("/app/order/new?fresh=1&when=scheduled"),
      icon: Clock,
      title: "Programar",
      subtitle: "Tu gas a la hora que elijas",
      cta: "Programar",
      variant: "primary",
      testid: "choose-schedule",
    },
    waHref && {
      key: "whatsapp",
      onClick: () => window.open(waHref, "_blank", "noopener,noreferrer"),
      icon: MessageCircle,
      title: "WhatsApp",
      subtitle: "Pide con un mensaje",
      cta: "Abrir",
      variant: "primary-light",
      testid: "choose-whatsapp",
    },
    {
      key: "orders",
      onClick: () => navigate("/app/orders"),
      icon: Package,
      title: "Mis pedidos",
      subtitle: "Repite o consulta",
      cta: "Ver",
      variant: "primary-dark",
      testid: "choose-my-orders",
    },
  ].filter(Boolean);
  const visible = primaries.filter((b) => b.available);
  const heroButtons = [...visible];
  for (const f of fallbacks) {
    if (heroButtons.length >= 3) break;
    if (!heroButtons.some((b) => b.key === f.key)) heroButtons.push(f);
  }
  // Final safety: always show at least 3 (the fallbacks list guarantees enough).
  while (heroButtons.length < 3 && fallbacks.length > 0) {
    heroButtons.push(fallbacks[heroButtons.length % fallbacks.length]);
  }

  return (
    <div className="min-h-screen bg-white">
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6 fade-in">
        {/* Company picker — bottom sheet with branded cards */}
        {companies.length > 1 && (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-neutral-500">Comprando en</p>
            <button
              type="button"
              onClick={() => setCompanyPickerOpen(true)}
              data-testid="company-picker"
              className="group flex items-center gap-2 rounded-full border border-neutral-200 pl-2 pr-3 py-1.5 hover:bg-neutral-50 tap-fade max-w-xs"
            >
              {currentCompany?.logo_url ? (
                <img src={currentCompany.logo_url} alt="" className="h-7 w-7 rounded-full object-cover" />
              ) : (
                <div className="h-7 w-7 rounded-full bg-company flex items-center justify-center text-[color:var(--company-primary-contrast)] text-xs font-bold">
                  {currentCompany?.name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <span className="text-sm font-semibold truncate">
                {currentCompany?.name || "Selecciona empresa"}
              </span>
              <ChevronDown size={14} className="text-neutral-500 shrink-0" />
            </button>

            <BottomSheet
              open={companyPickerOpen}
              onOpenChange={(o) => { setCompanyPickerOpen(o); if (!o) setCompanyQuery(""); }}
              title="Elige una empresa"
              description="Cambia dónde quieres comprar gas."
              testid="company-picker-sheet"
            >
                {companies.length > 5 && (
                  <div className="px-6 pb-3">
                    <div className="relative">
                      <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                      />
                      <input
                        type="text"
                        value={companyQuery}
                        onChange={(e) => setCompanyQuery(e.target.value)}
                        placeholder="Buscar empresa..."
                        autoFocus
                        data-testid="company-picker-search"
                        className="w-full h-11 pl-9 pr-3 rounded-xl border border-neutral-200 text-sm outline-none focus:border-neutral-900 transition-colors bg-neutral-50"
                      />
                    </div>
                  </div>
                )}
                <ul className="px-3 pb-4 space-y-2 overflow-y-auto max-h-[60vh]">
                  {(() => {
                    const q = companyQuery.trim().toLowerCase();
                    const filtered = q
                      ? companies.filter((c) => (c.name || "").toLowerCase().includes(q) || (c.slogan || "").toLowerCase().includes(q))
                      : companies;
                    if (filtered.length === 0) {
                      return (
                        <li className="py-8 text-center text-sm text-neutral-500" data-testid="company-picker-empty">
                          Sin resultados para "{companyQuery}"
                        </li>
                      );
                    }
                    return filtered.map((c) => {
                    const isCurrent = c.company_id === currentCompany?.company_id;
                    const primary = c.primary_color || c.colors?.primary || "#111";
                    return (
                      <li key={c.company_id}>
                        <button
                          type="button"
                          onClick={() => {
                            setCurrentCompany(c);
                            setCompanyPickerOpen(false);
                            setCompanyQuery("");
                          }}
                          data-testid={`company-picker-item-${c.company_id}`}
                          className={`w-full flex items-center gap-3 p-3 rounded-2xl border transition-all tap-fade text-left ${
                            isCurrent
                              ? "border-neutral-900 bg-neutral-50"
                              : "border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50"
                          }`}
                        >
                          {c.logo_url ? (
                            <img src={c.logo_url} alt="" className="h-12 w-12 rounded-xl object-cover shrink-0" />
                          ) : (
                            <div
                              className="h-12 w-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shrink-0"
                              style={{ background: primary }}
                            >
                              {c.name?.[0]?.toUpperCase() || "?"}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-display font-bold text-base truncate">{c.name}</p>
                            {c.slogan && (
                              <p className="text-xs text-neutral-500 truncate">{c.slogan}</p>
                            )}
                          </div>
                          {isCurrent && (
                            <span
                              className="h-7 w-7 rounded-full flex items-center justify-center text-white shrink-0"
                              style={{ background: primary }}
                              aria-label="Seleccionada"
                            >
                              <Check size={14} strokeWidth={3} />
                            </span>
                          )}
                        </button>
                      </li>
                    );
                    });
                  })()}
                </ul>
            </BottomSheet>
          </div>
        )}

        {/* Closed / closed-today banner */}
        {!status.open && (
          <div
            className={`rounded-2xl border p-4 flex items-start gap-3 ${status.mode === "permanent" ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}
            data-testid="closed-banner"
          >
            <div className={`shrink-0 h-8 w-8 rounded-full flex items-center justify-center font-bold ${status.mode === "permanent" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>!</div>
            <div className="flex-1 min-w-0">
              <p className={`font-semibold ${status.mode === "permanent" ? "text-red-800" : "text-amber-900"}`}>{status.reason}</p>
              <p className={`text-sm mt-0.5 ${status.mode === "permanent" ? "text-red-700" : "text-amber-800"}`}>
                {status.mode === "permanent"
                  ? "Esta tienda no está recibiendo pedidos."
                  : status.mode === "today"
                    ? (nextOpenText
                        ? `Reabrimos ${nextOpenText}. Puedes programar tu pedido desde hoy.`
                        : "Puedes programar tu pedido para un día disponible.")
                    : (nextOpenText
                        ? `Abrimos ${nextOpenText}. Puedes programar tu pedido.`
                        : "Puedes programar tu pedido para un día abierto.")}
              </p>
            </div>
            {status.mode !== "permanent" && (
              <Button
                size="sm"
                className="shrink-0 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90"
                data-testid="schedule-from-banner-btn"
                onClick={() => setChooseOpen(true)}
              >
                Programar
              </Button>
            )}
          </div>
        )}

        {/* HERO ZONE: "¿Qué necesitas hoy?" is the primary CTA, offer strip sits below */}
        <section>
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">¿Qué necesitas hoy?</h2>
              <p className="text-sm sm:text-base text-neutral-500 mt-1">Elige una opción y te lo llevamos en minutos.</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            {!heroLoaded
              ? Array.from({ length: 3 }).map((_, i) => <HeroSkeleton key={i} />)
              : heroButtons.map((btn) => <HeroButton key={btn.key} {...btn} />)}
          </div>
        </section>

        {/* Ofertas — now slimmer strip beneath the hero */}
        {offers.length > 0 && <OfferCarousel offers={offers} />}

        {/* Atajo "Pedir a mi dirección más usada" — sólo se muestra
            cuando el backend devuelve una dirección con orders_count >= 2
            para no convertirlo en ruido para clientes nuevos. El clic
            va directo al wizard con esa dirección pre-seleccionada; si
            tiene `preferred_company_id`, el wizard conmuta solo a la
            empresa correcta. */}
        {mostUsedAddr && (mostUsedAddr.orders_count || 0) >= 2 && (() => {
          const last = mostUsedAddr.last_order;
          const item = last?.items?.[0];
          const extraItems = Math.max(0, (last?.items?.length || 0) - 1);
          return (
          <section
            className="rounded-2xl border border-neutral-200 bg-white p-4 sm:p-5 flex items-center gap-3 sm:gap-4 hover:border-neutral-400 transition cursor-pointer tap-fade"
            onClick={() => {
              const params = new URLSearchParams({
                addr: mostUsedAddr.address_id,
                fresh: "1",
              });
              // Pasamos TODOS los items del último pedido, no sólo el
              // primero. Formato compacto: `items=pid1:qty1,pid2:qty2`.
              // El wizard los parsea y precarga el carrito completo.
              const allItems = (last?.items || []).filter((it) => it?.product_id && Number(it.quantity) > 0);
              if (allItems.length > 0) {
                const encoded = allItems.map((it) => `${it.product_id}:${it.quantity}`).join(",");
                params.set("items", encoded);
                // Además mandamos kind del primero (para filtrar el canal
                // si el cliente da "Atrás") y el precio unitario del
                // primero como referencia para el toast de cambio de
                // precio. Si hay mezcla de kinds, kind queda del primero
                // — el breadcrumb del wizard mostrará todos igualmente.
                const first = allItems[0];
                if (first.kind) params.set("kind", first.kind);
                if (first.unit_price != null) params.set("last_price", String(first.unit_price));
              }
              // Forzamos la empresa del ÚLTIMO pedido (no la preferida
              // de la dirección). El cliente espera que "Pedir otra vez"
              // repita EXACTAMENTE lo mismo que pidió la última vez,
              // incluyendo la tienda. El wizard conmutará si hace falta.
              if (last?.company_id) {
                params.set("company", last.company_id);
              }
              const url = `/app/order/new?${params.toString()}`;
              // Si la empresa del último pedido difiere de la actual,
              // abrimos un BottomSheet para confirmar el cambio (pide
              // claridad: el cliente sabe que entra al catálogo de
              // otra tienda y que los precios pueden haber cambiado).
              // Si es la misma empresa, navegamos directo sin fricción.
              if (last?.company_id && last.company_id !== currentCompany?.company_id) {
                const target = companies.find((c) => c.company_id === last.company_id);
                if (target) {
                  setReorderConfirm({ url, targetCompany: target, items: last.items || [] });
                  return;
                }
              }
              navigate(url);
            }}
            data-testid="home-most-used-address"
          >
            <div
              className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: "var(--company-primary)", color: "var(--company-primary-contrast)" }}
            >
              <Heart size={20} fill="currentColor" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Pedir otra vez</p>
              <p className="font-semibold truncate">
                Pedir a {mostUsedAddr.label}
                <span className="text-neutral-500 font-normal"> · {mostUsedAddr.orders_count} pedido{mostUsedAddr.orders_count === 1 ? "" : "s"}</span>
              </p>
              <p className="text-xs text-neutral-500 truncate">
                {item ? (
                  <>
                    Último: <span className="font-medium text-neutral-700">{Number(item.quantity).toLocaleString("es-MX", { maximumFractionDigits: 2 })}× {item.name}</span>
                    {extraItems > 0 && <span className="text-neutral-400"> +{extraItems} más</span>}
                  </>
                ) : mostUsedAddr.full_address}
              </p>
            </div>
            <ArrowRight size={18} className="text-neutral-400 shrink-0" />
          </section>
          );
        })()}

        {mostOrdered.length > 0 && (
          <section>
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="font-display text-lg font-bold">Los más pedidos</h3>
              <span className="text-xs text-neutral-400">Tu historial</span>
            </div>
            <ProductCarousel products={mostOrdered} onPick={pickProduct} testIdPrefix="top" size="sm" />
          </section>
        )}

        {catalog.length > 0 && (
          <section>
            <h3 className="font-display text-lg font-bold mb-3">Productos disponibles</h3>
            <ProductCarousel products={catalog} onPick={pickProduct} testIdPrefix="catalog" size="sm" />
          </section>
        )}

        <div className="flex flex-col items-center gap-3 pt-4">
          <Button
            size="lg"
            className="h-14 px-8 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90 rounded-xl font-semibold"
            data-testid="start-order-btn"
            onClick={() => setChooseOpen(true)}
          >
            Empezar pedido
          </Button>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            {currentCompany?.whatsapp && whatsappUrl(currentCompany.whatsapp) && (
              <a
                href={whatsappUrl(currentCompany.whatsapp, waTemplates.clientToCompany(currentCompany.name))}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-700 hover:text-green-800 hover:underline"
                data-testid="order-by-whatsapp-btn"
              >
                <MessageCircle size={14} />
                o pide por WhatsApp
              </a>
            )}
          </div>
        </div>

        {/* Confirmación de cambio de empresa al reordenar */}
        <BottomSheet
          open={!!reorderConfirm}
          onOpenChange={(o) => { if (!o) setReorderConfirm(null); }}
          title="Cambiamos a otra tienda"
          description={
            reorderConfirm
              ? `Este pedido lo hiciste con ${reorderConfirm.targetCompany?.name}. Para repetirlo cargaremos su catálogo.`
              : ""
          }
          testid="reorder-confirm-sheet"
        >
          <div className="px-6 pb-6">
            {reorderConfirm && (
              <div className="space-y-4">
                {/* Preview empresa destino */}
                <div className="flex items-center gap-3 p-3 rounded-2xl border border-neutral-200 bg-neutral-50">
                  {reorderConfirm.targetCompany?.logo_url ? (
                    <img
                      src={reorderConfirm.targetCompany.logo_url}
                      alt=""
                      className="h-12 w-12 rounded-xl object-cover shrink-0"
                    />
                  ) : (
                    <div
                      className="h-12 w-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shrink-0"
                      style={{ background: reorderConfirm.targetCompany?.primary_color || "#111" }}
                    >
                      {reorderConfirm.targetCompany?.name?.[0]?.toUpperCase() || "?"}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-bold text-base truncate">{reorderConfirm.targetCompany?.name}</p>
                    {reorderConfirm.targetCompany?.slogan && (
                      <p className="text-xs text-neutral-500 truncate">{reorderConfirm.targetCompany.slogan}</p>
                    )}
                  </div>
                </div>

                {/* Productos que se pre-cargarán (cantidad, no precio: el precio se valida en la tienda nueva) */}
                {reorderConfirm.items?.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Se repetirá</p>
                    <ul className="space-y-1.5">
                      {reorderConfirm.items.map((it, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <span className="font-semibold tabular-nums w-10 shrink-0">{Number(it.quantity).toLocaleString("es-MX", { maximumFractionDigits: 2 })}×</span>
                          <span className="truncate">{it.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Aviso de precio */}
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <p className="font-semibold">Los precios pueden haber cambiado</p>
                  <p className="text-xs mt-0.5 text-amber-800">
                    Llevamos la cantidad de tu pedido anterior; el monto se recalculará con los precios actuales de {reorderConfirm.targetCompany?.name}.
                  </p>
                </div>

                {/* Acciones */}
                <div className="flex gap-2 pt-1">
                  <Button
                    variant="outline"
                    className="flex-1 h-12"
                    onClick={() => setReorderConfirm(null)}
                    data-testid="reorder-confirm-cancel"
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="flex-1 h-12 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90 font-semibold"
                    onClick={() => {
                      const url = reorderConfirm.url;
                      setReorderConfirm(null);
                      navigate(url);
                    }}
                    data-testid="reorder-confirm-accept"
                  >
                    Continuar
                  </Button>
                </div>
              </div>
            )}
          </div>
        </BottomSheet>

        {/* Stations sheet — physical locations + estacionario products */}
        <BottomSheet
          open={stationsOpen}
          onOpenChange={setStationsOpen}
          title="Nuestras estaciones"
          description="Visítanos directamente en cualquiera de estos puntos."
          testid="stations-sheet"
        >
          <div className="px-6 pb-6 overflow-y-auto max-h-[70vh]">
            <div className="max-w-3xl mx-auto">
              {stations.length > 0 && (
                <div className="mt-2 grid sm:grid-cols-2 gap-3">
                  {stations.map((s) => (
                    <div key={s.station_id} className="rounded-2xl border border-neutral-200 p-4 bg-white" data-testid={`station-card-${s.station_id}`}>
                      <p className="font-semibold flex items-center gap-2"><MapPin size={14} className="text-company" />{s.name}</p>
                      {s.full_address && <p className="text-sm text-neutral-500 mt-1">{s.full_address}</p>}
                      <div className="flex gap-3 mt-2 text-xs">
                        {s.phone && <a href={`tel:${s.phone}`} className="font-semibold text-neutral-700 hover:underline">{s.phone}</a>}
                        {s.whatsapp && whatsappUrl(s.whatsapp) && (
                          <a href={whatsappUrl(s.whatsapp)} target="_blank" rel="noreferrer" className="font-semibold text-green-700 hover:underline">WhatsApp</a>
                        )}
                        {s.lat && s.lng && (
                          <a href={`https://www.google.com/maps/search/?api=1&query=${s.lat},${s.lng}`} target="_blank" rel="noreferrer" className="font-semibold text-[color:var(--company-primary)] hover:underline">Ver en mapa</a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {stationProducts.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-display text-lg font-bold mb-3">Productos en estación</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {stationProducts.map((p) => (
                      <div key={p.product_id} className="rounded-xl border border-neutral-200 p-3 bg-white" data-testid={`station-product-${p.product_id}`}>
                        <p className="font-semibold text-sm leading-tight line-clamp-2">{p.name}</p>
                        <p className="text-[11px] text-neutral-500 mt-1">Solo en estación</p>
                        <p className="font-display font-extrabold text-lg mt-1">${Number(p.price).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stations.length === 0 && stationProducts.length === 0 && (
                <p className="mt-6 text-sm text-neutral-500 text-center">Aún no hay estaciones configuradas.</p>
              )}
            </div>
          </div>
        </BottomSheet>

        {/* Bottom sheet for product kind selection (mobile + desktop) */}
        <BottomSheet
          open={chooseOpen}
          onOpenChange={setChooseOpen}
          title="¿Qué vas a pedir?"
          description="Elige el tipo de producto para continuar."
          testid="start-order-sheet"
        >
          <div className="px-6 pb-6">
            <div className="mt-2 grid grid-cols-2 gap-3 max-w-2xl mx-auto">
              <button
                type="button"
                onClick={() => { setChooseOpen(false); navigate("/app/order/new?kind=bulk&fresh=1"); }}
                className="group rounded-2xl border border-neutral-200 p-5 text-left hover:border-company hover:shadow-md transition-all bg-white"
                data-testid="sheet-choose-bulk"
              >
                <Droplets size={26} className="text-company" />
                <p className="mt-3 font-display font-extrabold text-xl">Gas</p>
                <p className="text-xs text-neutral-500 mt-0.5">Litros o kilogramos</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold">
                  Elegir <ArrowRight size={14} />
                </span>
              </button>
              <button
                type="button"
                onClick={() => { setChooseOpen(false); navigate("/app/order/new?kind=cylinder&fresh=1"); }}
                className="group rounded-2xl border border-neutral-200 p-5 text-left hover:border-company hover:shadow-md transition-all bg-white"
                data-testid="sheet-choose-cylinder"
              >
                <Flame size={26} className="text-company" />
                <p className="mt-3 font-display font-extrabold text-xl">Cilindros</p>
                <p className="text-xs text-neutral-500 mt-0.5">20 / 35 / 45 kg</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold">
                  Elegir <ArrowRight size={14} />
                </span>
              </button>
            </div>
          </div>
        </BottomSheet>
      </main>
    </div>
  );
}

// ---------- HeroButton ----------
// Card inspired by app store listings: large product image at top, big
// title below, price bottom-left and a heart "favorite" toggle bottom-right.
// The image comes from the representative product of each kind (bulk /
// cylinder / station). If the company has no image for that kind, we fall
// back to a subtle branded gradient with the lucide icon.
// Gradiente único del color de la empresa con sombras suaves para
// dar profundidad, igual al estilo que tenían las "Estaciones" antes.
const HERO_FALLBACK_GRADIENTS = {
  primary:
    "bg-[linear-gradient(135deg,color-mix(in_oklab,var(--company-primary)_75%,black)_0%,var(--company-primary)_60%,color-mix(in_oklab,var(--company-primary)_40%,black)_100%)]",
  // Aliases por compat con código existente — todos pintan el mismo gradient.
  "primary-light":
    "bg-[linear-gradient(135deg,color-mix(in_oklab,var(--company-primary)_75%,black)_0%,var(--company-primary)_60%,color-mix(in_oklab,var(--company-primary)_40%,black)_100%)]",
  "primary-dark":
    "bg-[linear-gradient(135deg,color-mix(in_oklab,var(--company-primary)_75%,black)_0%,var(--company-primary)_60%,color-mix(in_oklab,var(--company-primary)_40%,black)_100%)]",
};

const FAV_KEY = "gas2go:favorite-kinds";
function readFavSet() {
  try {
    const raw = JSON.parse(localStorage.getItem(FAV_KEY) || "[]");
    return new Set(Array.isArray(raw) ? raw : []);
  } catch {
    return new Set();
  }
}
function writeFavSet(set) {
  try { localStorage.setItem(FAV_KEY, JSON.stringify(Array.from(set))); } catch { /* ignore */ }
}

function HeroButton({ onClick, icon: Icon, title, subtitle, variant, testid, image, price, productId }) {
  const favId = productId || title;
  const [fav, setFav] = React.useState(() => readFavSet().has(favId));
  const toggleFav = (e) => {
    e.stopPropagation();
    const s = readFavSet();
    if (s.has(favId)) s.delete(favId); else s.add(favId);
    writeFavSet(s);
    setFav(s.has(favId));
  };
  const gradient = HERO_FALLBACK_GRADIENTS[variant] || HERO_FALLBACK_GRADIENTS.primary;

  const ImageBlock = (
    <div className={`relative w-full h-full ${image ? "" : gradient} overflow-hidden flex items-center justify-center`}>
      {image ? (
        <>
          <img
            src={image}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          <span className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20 pointer-events-none" aria-hidden />
        </>
      ) : (
        <Icon className="text-white/90 opacity-90 relative z-10 h-10 w-10 sm:h-8 sm:w-8 md:h-10 md:w-10 lg:h-14 lg:w-14 shrink-0" />
      )}
    </div>
  );

  const InfoBlock = (
    <div className="flex-1 flex flex-col justify-between gap-2 sm:gap-3 p-3 sm:p-3 lg:p-5 bg-company text-[color:var(--company-primary-contrast)] min-w-0">
      <div className="min-w-0">
        <h3 className="font-display font-extrabold text-xl sm:text-base md:text-lg lg:text-xl xl:text-2xl leading-tight tracking-tight truncate">{title}</h3>
        <p className="text-xs sm:text-[11px] md:text-xs opacity-70 mt-0.5 truncate">{subtitle}</p>
      </div>
      <div className="flex items-end justify-between gap-2">
        {price != null && price > 0 ? (
          <div className="flex items-end gap-1 min-w-0">
            <span className="text-[10px] sm:text-[10px] opacity-70 leading-tight pb-1">desde</span>
            <p className="font-display font-extrabold text-xl sm:text-base md:text-lg lg:text-xl xl:text-2xl tabular-nums leading-none truncate">
              <span className="opacity-70 text-sm align-top mr-0.5">$</span>
              {Number(price).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        ) : (
          <p className="font-display font-semibold text-sm sm:text-xs md:text-sm opacity-90 truncate">
            Ver opciones
          </p>
        )}
        <span
          role="button"
          tabIndex={0}
          onClick={toggleFav}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleFav(e); } }}
          aria-label={fav ? "Quitar de favoritos" : "Agregar a favoritos"}
          data-testid={`${testid}-fav`}
          className={`inline-flex items-center justify-center h-9 w-9 sm:h-7 sm:w-7 md:h-8 md:w-8 lg:h-10 lg:w-10 rounded-xl border transition-colors shrink-0 ${
            fav
              ? "bg-white/15 border-white/70"
              : "bg-transparent border-white/30 hover:border-white/60"
          }`}
        >
          <Heart size={14} strokeWidth={2.2} fill={fav ? "currentColor" : "none"} />
        </span>
      </div>
    </div>
  );

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="group relative text-left rounded-3xl overflow-hidden shadow-sm hover:shadow-xl sm:hover:-translate-y-1 transition-all tap-fade flex h-28 sm:h-32 lg:h-40"
    >
      {/* Info left, image right — same layout on mobile & desktop.
          Min-w-0 lets flex actually shrink the info column and honor
          `truncate` on long titles/prices. Image column keeps a fixed
          width — narrow at sm/md (where cards are 3-col and tight) and
          generous at lg+ (cards have breathing room). */}
      <div className="flex-1 flex flex-col min-w-0">
        {InfoBlock}
      </div>
      <div className="w-28 sm:w-20 md:w-24 lg:w-32 xl:w-40 shrink-0 relative">
        {ImageBlock}
      </div>
    </button>
  );
}

// Placeholder shown while the products/stations fetches are in-flight so the
// user doesn't see fallback buttons pop in and get replaced. Matches the real
// button footprint to avoid layout shift.
function HeroSkeleton() {
  return (
    <div
      aria-hidden
      data-testid="hero-skeleton"
      className="relative rounded-3xl overflow-hidden bg-neutral-100 h-28 sm:h-32 lg:h-40 animate-pulse"
    >
      <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.6)_50%,transparent_100%)] skeleton-shimmer" />
    </div>
  );
}
